import { defineStore } from 'pinia'
import tinycolor from 'tinycolor2'
import { omit } from 'lodash'
import { Slide, SlideTheme, PPTElement, PPTAnimation, Rule } from '@/types/slides'
import { LoggedInUser } from '@/types/user'
import { slides } from '@/mocks/slides'
import { theme } from '@/mocks/theme'
import { layouts } from '@/mocks/layout'
import {useAuthStore} from "@/store/auth";


interface RemoveElementPropData {
  id: string
  propName: string | string[]
}

interface UpdateElementData {
  id: string | string[]
  props: Partial<PPTElement>
}

interface FormatedAnimation {
  animations: PPTAnimation[]
  autoNext: boolean
}

export interface SlidesState {
  path: string
  theme: SlideTheme
  slides: Slide[]
  librarySlides: Slide[]
  slideIndex: number
  viewportRatio: number
  valueModelId: number
  slideName: string
  deckTitle: string
  author: LoggedInUser | null
  created: string | null,
  updated: string | null
  rules: Rule[] | []
  rulesEnabled: boolean
  currency: string;
  locale: string;
}

export const useSlidesStore = defineStore('slides', {
  state: (): SlidesState => ({
    path: '',
    theme: theme, // theme style
    slides: slides, // slide page data
    librarySlides: [],
    slideIndex: 0, // current page index
    viewportRatio: 0.5625, // Viewable area ratio, default 16:9
    valueModelId: 0,
    slideName: '',
    deckTitle: '',
    author: null,
    created: null,
    updated: null,
    rules: [],
    rulesEnabled: true,
    currency: 'USD',
    locale: 'en-US'
  }),

  getters: {
    currentSlide(state) {
      return state.slides[state.slideIndex]
    },

    currentSlideAnimations(state) {
      const currentSlide = state.slides[state.slideIndex]
      if (!currentSlide?.animations) return []

      const els = currentSlide.elements
      const elIds = els.map(el => el.id)
      return currentSlide.animations.filter(animation => elIds.includes(animation.elId))
    },

    // Formatted current page animation
    // Merge items with a trigger condition of "simultaneously with previous animation" up into the same position in the sequence
    // Add an automatic downward execution mark for the previous item whose trigger condition is "after the last animation" item
    formattedAnimations(state) {
      const currentSlide = state.slides[state.slideIndex]
      if (!currentSlide?.animations) return []

      const els = currentSlide.elements
      const elIds = els.map(el => el.id)
      const animations = currentSlide.animations.filter(animation => elIds.includes(animation.elId))


      const formattedAnimations: FormatedAnimation[] = []
      for (const animation of animations) {
        if (animation.trigger === 'click' || !formattedAnimations.length) {
          formattedAnimations.push({ animations: [animation], autoNext: false })
        }
        else if (animation.trigger === 'meantime') {
          const last = formattedAnimations[formattedAnimations.length - 1]
          last.animations = last.animations.filter(item => item.elId !== animation.elId)
          last.animations.push(animation)
          formattedAnimations[formattedAnimations.length - 1] = last
        }
        else if (animation.trigger === 'auto') {
          const last = formattedAnimations[formattedAnimations.length - 1]
          last.autoNext = true
          formattedAnimations[formattedAnimations.length - 1] = last
          formattedAnimations.push({ animations: [animation], autoNext: false })
        }
      }
      return formattedAnimations
    },

    layouts(state) {
      const {
        themeColor,
        fontColor,
        fontName,
        backgroundColor,
      } = state.theme

      const subColor = tinycolor(fontColor).isDark() ? 'rgba(230, 230, 230, 0.5)' : 'rgba(180, 180, 180, 0.5)'

      const layoutsString = JSON.stringify(layouts)
        .replaceAll('{{themeColor}}', themeColor)
        .replaceAll('{{fontColor}}', fontColor)
        .replaceAll('{{fontName}}', fontName)
        .replaceAll('{{backgroundColor}}', backgroundColor)
        .replaceAll('{{subColor}}', subColor)

      return JSON.parse(layoutsString)
    },
  },

  actions: {

    setTheme(themeProps: Partial<SlideTheme>) {
      this.theme = { ...this.theme, ...themeProps }
    },

    setViewportRatio(viewportRatio: number) {
      this.viewportRatio = viewportRatio
    },

    setSlides(slides: Slide[]) {
      this.slides = slides
    },

    addLibrarySlides(librarySlides: Slide[]) {
      this.librarySlides.push(...librarySlides)
    },

    clearLibrarySlides() {
      this.librarySlides = []
    },

    setValueModelId(valueModeId: number) {
      this.valueModelId = valueModeId
    },

    addSlide(slide: Slide | Slide[]) {
      const slides = Array.isArray(slide) ? slide : [slide]
      const addIndex = this.slideIndex + 1
      this.slides.splice(addIndex, 0, ...slides)
      this.slideIndex = addIndex
    },

    updateSlideName(slideId: string, name: string) {
      console.log('updating slide name', slideId, name)
    },

    updateSlide(props: Partial<Slide>) {
      const slideIndex = this.slideIndex
      const slideCopy = { ...this.slides[slideIndex], ...props }
      // handling elements specially because we're doing a partial save on that too
      if ('elements' in props && props.elements) {
        slideCopy.elements = JSON.parse(JSON.stringify(this.slides[slideIndex].elements))
        const map = new Map(props.elements.map(item => [item.id, item]))
        slideCopy.elements = slideCopy.elements.map(item => map.get(item.id) || item)
      }

      this.slides[slideIndex] = slideCopy
    },

    updateSlideWithElements(props: Partial<Slide>) {
      const slideIndex = this.slideIndex
      this.slides[slideIndex] = { ...this.slides[slideIndex], ...props }
    },

    deleteSlide(slideId: string | string[]) {
      const slidesId = Array.isArray(slideId) ? slideId : [slideId]

      const deleteSlidesIndex = []
      for (let i = 0; i < slidesId.length; i++) {
        const index = this.slides.findIndex(item => item.id === slidesId[i])
        deleteSlidesIndex.push(index)
      }
      let newIndex = Math.min(...deleteSlidesIndex)

      const maxIndex = this.slides.length - slidesId.length - 1
      if (newIndex > maxIndex) newIndex = maxIndex

      this.slideIndex = newIndex
      this.slides = this.slides.filter(item => !slidesId.includes(item.id))
    },

    updateSlideIndex(index: number) {
      this.slideIndex = index
    },

    getElementsByType(type: string) {
      return this.slides[this.slideIndex].elements.filter((element) => element.type === type)
    },

    getElementsByTypeAllSlides(type: string) {
      const allElements = this.slides
          .flatMap((slide) => slide.elements)
          .concat(this.librarySlides.flatMap((slide) => slide.elements));

      return allElements.filter((element) => element.type === type);
    },

    addElement(element: PPTElement | PPTElement[]) {
      const elements = Array.isArray(element) ? element : [element]
      const currentSlideEls = this.slides[this.slideIndex].elements
      const newEls = [...currentSlideEls, ...elements]
      this.slides[this.slideIndex].elements = newEls
    },

    deleteElement(elementId: string | string[]) {
      const elementIdList = Array.isArray(elementId) ? elementId : [elementId]
      const currentSlideEls = this.slides[this.slideIndex].elements
      const newEls = currentSlideEls.filter(item => !elementIdList.includes(item.id))
      this.slides[this.slideIndex].elements = newEls
    },

    updateElement(data: UpdateElementData) {
      const { id, props } = data
      const elIdList = typeof id === 'string' ? [id] : id

      const slideIndex = this.slideIndex
      const slide = this.slides[slideIndex]
      const elements = slide.elements.map(el => {
        return elIdList.includes(el.id) ? { ...el, ...props } : el
      })
      this.slides[slideIndex].elements = (elements as PPTElement[])

    },

    removeElementProps(data: RemoveElementPropData) {
      const { id, propName } = data
      const propsNames = typeof propName === 'string' ? [propName] : propName

      const slideIndex = this.slideIndex
      const slide = this.slides[slideIndex]
      const elements = slide.elements.map(el => {
        return el.id === id ? omit(el, propsNames) : el
      })
      this.slides[slideIndex].elements = (elements as PPTElement[])
    },

  },
  // TODO: compare persisted data to see what changed
  // persist: true
})
